/**
 * The replay scrubber: a horizontal, multi-lane band over the visit window —
 * stage chapters, room presence, audio coverage, and one marker tick per
 * moment — with a draggable playhead. Rendering follows CaptureTimeline's
 * %-positioning approach; interaction adds pointer-driven seeking. The axis
 * is the piecewise `TimeScale` (built by the shell), so long idle gaps render
 * as narrow break markers instead of stretching the track.
 *
 * Material: each lane is a groove cut into the sheet (recess), stage chapters
 * are strips of card laid in it, rooms are label tape (physical places get
 * tape), audio coverage is the working highlighter's ink, and moments are pen
 * ticks - brian ink where Brian himself acted, espresso for everyone else.
 */
import { cn } from "@scribenote/brian-design-system";
import { formatMonthDay, formatTime } from "@utils/format-date";
import { useRef } from "react";

import type { MomentCategory, ReplayMoment, VisitReplay } from "./replayClient";
import { categoryInk, CATEGORY_LABEL } from "./replayTheme";
import { formatGap, ms, type TimeScale } from "./timeScale";

/** Faded ruling inside the lanes - the ticks are guides, not content. */
const HAIRLINE = "border-rule/60";

const LABEL_COL = "5.5rem";

/** Tick spacing that yields ~6–10 labeled ticks for the window. */
const tickStepMs = (spanMs: number): number => {
  const steps = [1, 2, 5, 10, 15, 30, 60, 120, 240].map((m) => m * 60_000);
  return steps.find((s) => spanMs / s <= 10) ?? steps[steps.length - 1];
};

const ticksFor = (start: number, end: number, step: number): number[] => {
  const first = Math.ceil(start / step) * step;
  const ticks: number[] = [];
  for (let t = first; t <= end; t += step) ticks.push(t);
  return ticks;
};

/**
 * When a stage chapter ends on the track: at its completion, at the next
 * chapter's start, or at the window end — whichever comes first. The
 * next-start cap is what keeps several never-completed stages (common on
 * stale visits) from all stretching to the window end and stacking their
 * labels; it also matches how the shell resolves the current stage name
 * (last stage entered wins).
 */
export const chapterEndMs = (
  completedAt: string | null,
  nextEnteredAt: string | null,
  windowEnd: number,
): number =>
  Math.min(
    completedAt ? ms(completedAt) : Number.POSITIVE_INFINITY,
    nextEnteredAt ? ms(nextEnteredAt) : Number.POSITIVE_INFINITY,
    windowEnd,
  );

const Lane = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <span className="w-[5.5rem] shrink-0 text-right text-annotation-caps text-annotation">
      {label}
    </span>
    <div className="relative h-7 flex-1 overflow-hidden rounded-paper bg-recess">
      {children}
    </div>
  </div>
);

export const ReplayTimeline = ({
  replay,
  scale,
  playhead,
  onSeek,
  visibleCategories,
}: {
  replay: VisitReplay;
  scale: TimeScale;
  playhead: number;
  onSeek: (t: number) => void;
  visibleCategories: Set<MomentCategory>;
}) => {
  const start = ms(replay.windowStart);
  const end = ms(replay.windowEnd);
  const pct = scale.pct;
  const step = tickStepMs(scale.activeMs);
  const ticks = scale.spans.flatMap((s) => ticksFor(s.start, s.end, step));
  // A window crossing midnight shows bare times that read as one day;
  // prefix the first tick of each new day with its date.
  const multiDay =
    new Date(ms(replay.windowStart)).toDateString() !==
    new Date(end).toDateString();
  // Every tick keeps its hairline, but a label is only printed when it has
  // ~a label's width of clear track since the previous printed thing. On a
  // heavily collapsed window (dozens of gap-separated spans) the spans pack
  // tighter than the labels, which otherwise smear into one unreadable line.
  // The gaps' duration notes share the same row and can't move, so they claim
  // their spot first and ticks yield to them.
  const labeledTicks = new Set<number>();
  {
    const slots = [
      ...scale.gaps.map((g) => ({
        left: (pct(g.start) + pct(g.end)) / 2,
        tick: null as number | null,
      })),
      ...ticks.map((tick) => ({ left: pct(tick), tick })),
    ].sort((a, b) => a.left - b.left);
    let lastLeft = Number.NEGATIVE_INFINITY;
    for (const slot of slots) {
      if (slot.tick === null) {
        lastLeft = slot.left;
      } else if (slot.left - lastLeft >= 7) {
        labeledTicks.add(slot.tick);
        lastLeft = slot.left;
      }
    }
  }
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const seekFromPointer = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fraction = Math.min(
      Math.max((clientX - rect.left) / rect.width, 0),
      1,
    );
    onSeek(scale.timeAt(fraction));
  };

  const stageBlocks = replay.stages
    .filter((s) => s.enteredAt !== null)
    .sort((a, b) => ms(a.enteredAt ?? "") - ms(b.enteredAt ?? ""));
  const moments = replay.moments.filter((m) =>
    visibleCategories.has(m.category),
  );
  const playheadLeft = `calc(${LABEL_COL} + 0.5rem + (100% - ${LABEL_COL} - 0.5rem) * ${(
    pct(playhead) / 100
  ).toFixed(6)})`;

  // The faded ruling shared by every lane. Rendered first in each lane so the
  // objects laid in the groove (stage card, room tape, audio ink, moment
  // ticks) sit on top of the ruling, not under it.
  const ruling = ticks.map((t) => (
    <div
      key={t}
      style={{ left: `${pct(t)}%` }}
      className={`pointer-events-none absolute inset-y-0 border-l ${HAIRLINE}`}
    />
  ));

  return (
    <div
      className="relative flex cursor-crosshair flex-col gap-1.5 select-none"
      onPointerDown={(e) => {
        // Only the track area seeks — a click on the lane-label column must
        // not clamp to fraction 0 and yank the playhead to the window start.
        const track = trackRef.current;
        if (!track || e.clientX < track.getBoundingClientRect().left) return;
        dragging.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        seekFromPointer(e.clientX);
      }}
      onPointerMove={(e) => {
        if (dragging.current) seekFromPointer(e.clientX);
      }}
      onPointerUp={() => {
        dragging.current = false;
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
    >
      {/* Tick labels — also the keyboard-operable slider surface */}
      <div className="flex gap-2">
        <span className="w-[5.5rem] shrink-0" />
        <div
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-label="Replay position"
          aria-valuemin={start}
          aria-valuemax={end}
          aria-valuenow={playhead}
          aria-valuetext={formatTime(new Date(playhead).toISOString())}
          onKeyDown={(e) => {
            // A step landing inside a collapsed gap snaps to the gap's far
            // edge (same jump playback does) — otherwise crossing a collapsed
            // multi-day gap takes thousands of presses.
            const STEP = 15_000;
            if (e.key === "ArrowLeft") {
              const t = playhead - STEP;
              onSeek(Math.max(scale.gapStart(t) ?? t, start));
            } else if (e.key === "ArrowRight") {
              const t = playhead + STEP;
              onSeek(Math.min(scale.gapEnd(t) ?? t, end));
            } else if (e.key === "Home") onSeek(start);
            else if (e.key === "End") onSeek(end);
            else return;
            e.preventDefault();
          }}
          className="relative h-4 flex-1 rounded-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {ticks
            .filter((tick) => labeledTicks.has(tick))
            .map((tick, i, printed) => {
              const left = pct(tick);
              // A tick sitting against a collapsed gap gets its label pushed
              // away from the break band, or a centered label (and its date
              // prefix) juts over the gap's duration note.
              const afterGap = scale.gaps.some((g) => {
                const d = left - pct(g.end);
                return d >= -0.1 && d < 2;
              });
              const beforeGap = scale.gaps.some((g) => {
                const d = pct(g.start) - left;
                return d >= -0.1 && d < 2;
              });
              const align =
                left < 2 || afterGap
                  ? ""
                  : left > 98 || beforeGap
                    ? "-translate-x-full"
                    : "-translate-x-1/2";
              const iso = new Date(tick).toISOString();
              const newDay =
                multiDay &&
                (i === 0 ||
                  new Date(tick).toDateString() !==
                    new Date(printed[i - 1]).toDateString());
              return (
                <span
                  key={tick}
                  style={{ left: `${left}%` }}
                  className={`absolute font-mono text-[10px] whitespace-nowrap text-annotation tabular-nums ${align}`}
                >
                  {newDay ? `${formatMonthDay(iso)} · ` : ""}
                  {formatTime(iso)}
                </span>
              );
            })}
        </div>
      </div>

      {/* Stage chapters: strips of card stock laid into the groove. The
          stage is the visit's own (human) flow, so the ink is espresso. */}
      <Lane label="Stage">
        {ruling}
        {stageBlocks.map((stage, i) => {
          const from = pct(ms(stage.enteredAt ?? replay.windowStart));
          const to = pct(
            chapterEndMs(
              stage.completedAt,
              stageBlocks[i + 1]?.enteredAt ?? null,
              end,
            ),
          );
          return (
            <div
              key={stage.uuid}
              className="absolute inset-y-0 flex items-center overflow-hidden border-r border-rule bg-card px-1.5"
              style={{ left: `${from}%`, width: `${to - from}%` }}
            >
              <span className="truncate text-[10px] font-medium text-ink-body">
                {stage.name}
              </span>
            </div>
          );
        })}
      </Lane>

      {/* Room presence: rooms are physical places, so each stay is a strip
          of label tape stuck across the groove. */}
      <Lane label="Room">
        {ruling}
        {replay.stays.map((stay, i) => {
          const from = pct(ms(stay.enteredAt));
          const to = pct(stay.leftAt ? ms(stay.leftAt) : end);
          return (
            <div
              key={`${stay.enteredAt}-${i}`}
              className="absolute inset-y-1 flex items-center overflow-hidden rounded-[2px] bg-ink px-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_-1px_0_rgba(0,0,0,0.4)]"
              style={{ left: `${from}%`, width: `${to - from}%` }}
            >
              <span className="truncate font-mono text-[9px] font-medium tracking-[0.13em] uppercase text-ink-inverse">
                {stay.roomName}
              </span>
            </div>
          );
        })}
      </Lane>

      {/* Audio coverage: the working highlighter's ink, standing alone as a
          wordless line (the wash rule keeps washes on words). */}
      <Lane label="Audio">
        {ruling}
        {replay.capture.map((segment) => {
          const from = pct(ms(segment.start));
          const to = pct(ms(segment.end));
          return (
            <div
              key={`${segment.start}-${segment.end}`}
              className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-ink-working"
              style={{
                left: `${from}%`,
                width: `${Math.max(to - from, 0.3)}%`,
              }}
            />
          );
        })}
      </Lane>

      {/* Moment markers: pen ticks in their category's ink (WEB-3366), so the
          interleaved kinds separate at a glance. The word is always within
          reach: tooltip, accessible name, and the matching chip below. */}
      <Lane label="Moments">
        {ruling}
        {moments.map((m: ReplayMoment, i) => (
          <button
            key={`${m.at}-${m.kind}-${i}`}
            type="button"
            title={m.title}
            aria-label={`${CATEGORY_LABEL[m.category]}: ${m.title}, ${formatTime(m.at)}`}
            onClick={() => onSeek(ms(m.at))}
            // 20px hit target around the 4px tick — the tick alone is below
            // any usable touch/click size.
            className="absolute top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            style={{ left: `${pct(ms(m.at))}%` }}
          >
            <span
              aria-hidden="true"
              className={cn(
                "h-4 w-[3px] rounded-full",
                categoryInk(m.category).dot,
              )}
            />
          </button>
        ))}
      </Lane>

      {/* Collapsed idle gaps: narrow break bands across every lane */}
      {scale.gaps.map((gap) => {
        const fromFraction = pct(gap.start) / 100;
        const widthFraction = (pct(gap.end) - pct(gap.start)) / 100;
        const track = `(100% - ${LABEL_COL} - 0.5rem)`;
        return (
          <div
            key={gap.start}
            className="pointer-events-none absolute top-4 bottom-0 z-[5] border-x border-dashed border-rule bg-[repeating-linear-gradient(135deg,transparent,transparent_3px,var(--color-rule)_3px,var(--color-rule)_4px)] opacity-70"
            style={{
              left: `calc(${LABEL_COL} + 0.5rem + ${track} * ${fromFraction.toFixed(6)})`,
              width: `calc(${track} * ${widthFraction.toFixed(6)})`,
            }}
          >
            {/* Paper behind the duration so a tick label landing at the gap's
                edge is knocked out instead of colliding. */}
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-paper px-0.5 font-mono text-[9px] whitespace-nowrap text-annotation">
              ⋯ {formatGap(gap.end - gap.start)}
            </span>
          </div>
        );
      })}

      {/* The playhead is the reader's needle: espresso ink with a paper halo so it
          stays visible crossing the dark room tape. Position, not status, so
          it takes no status color. */}
      <div
        className="pointer-events-none absolute top-4 bottom-0 z-10 w-0.5 bg-ink shadow-[0_0_0_1px_var(--color-paper)]"
        style={{ left: playheadLeft }}
      >
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 rounded-paper bg-ink px-1 py-px font-mono text-[9px] whitespace-nowrap text-ink-inverse tabular-nums">
          {formatTime(new Date(playhead).toISOString())}
        </div>
      </div>
    </div>
  );
};
